import React, { useState, useEffect, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import types from './types';
import percentToHex from '../PercentToHex';
import UIEditorSubcomponent from '../UIEditorSubcomponent';
import { AspetMultipliers } from '../PreviewAspect';
import { QuizType } from '../../../globals';
import Spacer from '../../layout/Spacer';
import Panel from '../../layout/Panel';
import ProgressBarCircular from '../../layout/ProgressBarCircular';
import QuizManager from '../../../classes/QuizManager';
import MathUtils from '../../../classes/utilities/MathUtils';
import ErrorHandler, { ErrorCategory, ErrorSeverity } from '../../../classes/utilities/ErrorHandling';

export default function UIScoreNumber(props)
{
	let properties = {
		'background color': {
			type: 'color',
			default: '#313131'
		},
		// 'background alpha': {
		// 	type: 'range',
		// 	default: 100
		// },
		'main text color': {
			type: 'color',
			default: '#ffffff'
		},
		'main text alpha': {
			type: 'range',
			default: 100
		},
		'secondary text color': {
			type: 'color',
			default: '#ffffff'
		},
		'secondary text alpha': {
			type: 'range',
			default: 100
		},
	}

	if (props.getProperties)
	{
		return properties;
	}

	if (props.context === 'editor')
	{
		return (
			<UIEditorSubcomponent
				title={'General'}
				subcomponent={types.SCORE_NUMBER}
				properties={properties}
				{...props}
			/>
		)
	}
	else
	{
		const quizSettings = useSelector(store => store.checklistModule.quizSettings);
		const passingScore = useSelector(store => store.checklistModule.quizSettings.passingScore, shallowEqual);
		const previewAspect = useSelector(store => store.uiModule.previewAspect);

		const [scoreProgress, setScoreProgress] = useState(0);

		const barFillAudio = useRef(null);
		const scoreCounter = useRef(null);
		const rootRef = useRef(null);

		const data = props.data;
		const simulation = props.context === 'simulation';
		const backgroundColor = data['background color'].value;// + percentToHex(data['background alpha'].value);
		const mainTextColor = data['main text color'].value + percentToHex(data['main text alpha'].value);
		const secondaryTextColor = data['secondary text color'].value + percentToHex(data['secondary text alpha'].value);
		const score = QuizManager.calculateQuizScore();
		const isGradedQuiz = quizSettings.type === QuizType.GRADED;
		const anyGradedQuestions = QuizManager.areThereAnyGradedQuestions();
		const passed = score >= passingScore;
		const showWarnings = props.showWarnings ?? true;

		useEffect(() =>
		{
			barFillAudio.current = new Audio("quiz_score/CalculatingScore_v4_Loopable.wav");
			barFillAudio.current.loop = true;

			return () => 
			{
				clearInterval(scoreCounter.current);
				barFillAudio.current.pause();
			}
		}, []);

		useEffect(() =>
		{
			if(scoreProgress >= score)
			{
				clearInterval(scoreCounter.current);
				setScoreProgress(score);

				const spinHighlight = document.getElementById("quizResultSpinningHighlight");
				if (passed && spinHighlight) spinHighlight.style.display = "";

				const banner = document.getElementById("quizResultBannerRoot");
				if (banner) banner.style.display = "";

				// stop playing fill sounds
				barFillAudio.current.pause();

				// only animate during simulation and only if graded quiz is active
				if (simulation && isGradedQuiz)
				{
					if (spinHighlight) spinHighlight.classList.add("fadeInFast");
					if (banner) banner.classList.add("fadeInFast");

					// play completion sound
					new Audio(passed ? "quiz_score/ScorePass_v1.wav" : "quiz_score/ScoreFail_v1.wav").play();
				}
			}
		}, [scoreProgress]);

		useEffect(() =>
		{
			if(simulation && isGradedQuiz)
			{
				if(barFillAudio.current.paused)
				{
					const playPromise = barFillAudio.current.play();
					if (playPromise) 
					{
						playPromise.catch(error => 
						{
							//meant to catch "The play() request was interrupted by a call to pause()" errors
							ErrorHandler.reportError(error, ErrorCategory.General, ErrorSeverity.Warning);
						});
					}
				}

				scoreCounter.current = setInterval(() =>
				{
					setScoreProgress(prog => prog + 1);
				}, 16);
			}
		}, [props.context]);

		function drawScoreMarkerLine()
		{
			// 100% score is at 327 degrees, while 0% score is at 213 degrees
			// that means we have a total of 246 degrees to work with, as the progress bar is rotated 147 degrees
			// using the calculation of: a) 327-213 = 114; but then we need the opposite of that range, so b) abs(114 - 360) = 246
			// this is the range ot angles to work with from 0% to 100%
			// we flip the calculation here because higher angle goes counterclockwise and we need to go clockwise
			const angle = 246 * (1 - passingScore / 100) - 33;

			// the starting point of 27.5, 16 takes into account the larger sale of the score marker svg root
			// 16, 16 is the center of the svg viewbox which is 32x32
			const startPoint = MathUtils.rotatePointAroundPivot2D({ x: 16, y: 16 }, { x: 27.3, y: 16 }, angle);

			// flip marker facing when crossing the 50% score mark
			return `M ${startPoint.x} ${startPoint.y}` + (passingScore >= 50 ? " l 1.5 -2 l 2 0" : " l -1.5 -2 l -2 0");
		}

		function drawScoreText()
		{
			// start at the end of the marker line using the same coordinate technique with some offset
			// note that <50% we have manually adjusted the offset to account for label width - changing the scale will require tweaking of this value
			const angle = 246 * (1 - passingScore / 100) - 33;
			let startPoint = MathUtils.rotatePointAroundPivot2D({ x: 16, y: 16 }, { x: 27.5, y: 16 }, angle);
			startPoint.x += passingScore >= 50 ? 3.75 : -13.25;
			startPoint.y -= 2;

			// adjust for label text scaling
			const labelScale = 0.12;
			startPoint.x /= labelScale;
			startPoint.y /= labelScale;

			startPoint.x = isNaN(startPoint.x) ? 0 : startPoint.x;
			startPoint.y = isNaN(startPoint.y) ? 0 : startPoint.y;

			return (
				<text
					x={startPoint.x}
					y={startPoint.y}
					fill={secondaryTextColor}
					transform="scale(0.12)"
				>
					<tspan>Passing Grade</tspan>
					<tspan x={startPoint.x} dy="1.2em" fontWeight="bold">{passingScore}%</tspan>
				</text>
			);
		}

		return (
			<div id={"quizResultRoot"}
				ref={rootRef}
				className={'quizScoreRoot fullScreen'}
				style={{
					background: backgroundColor,
					overflow: "hidden",
				}}
				data-name={'ui-quiz-score-root'}
			>
				<div id={"quizResultSpinningHighlightWrapper"}
					className='fullScreen centerContent rotateRightSlow'
				>
					<img id={"quizResultSpinningHighlight"}
						src={"quiz_score/bg_rays.png"}
						key={'quiz_result_bg_image'}
						draggable={false}
						style={{
							display: "block",
						}}
					/>
				</div>
				<div id={"scoreContentRoot"}
					className={"fullScreen centerContent"}
					style={{
						paddingTop: "3%",
						fontSize: "10px",
					}}
				>
					<ProgressBarCircular progress={scoreProgress}
						size={rootRef.current?.getBoundingClientRect().height * 0.44}
						startAngle={-127}
						endAngle={255}
						showLabel={true}
						barColor={scoreProgress >= passingScore ? 'green' : 'red'}
						backgroundColor={"#464646"}
						strokeWidth={(simulation ? 12 : 10 * props.scale)}
						labelStyle={{
							color: mainTextColor,
							fontSize: (simulation ? 120 : 60 * props.scale) + "px",
							fontWeight: "bold",
						}}
						percentMarkStyle={{
							fontSize: (simulation ? 80 : 40 * props.scale) + "px",
							fontWeight: "bold",
						}}
					/>
					<svg id={"quizResultsScoreMarker"}
						className='scoreMarkerContainer'
						viewBox="0 0 32 32"
						xmlns="http://www.w3.org/2000/svg"
						height={"60%"}
						width={`${60 / AspetMultipliers[previewAspect]}%`}
						fill="none"
						style={{
							position: "absolute",
							overflow: "visible",
						}}
						transform-box={"fill-box"}
						transform-origin={"center center"}
					>
						<path className="scoreMarker"
							d={drawScoreMarkerLine()}
							stroke={secondaryTextColor}
							strokeWidth={"0.15px"}
						/>
						{drawScoreText()}
					</svg>
				</div>
				<div id={"quizResultBannerRoot"}
					className="resultsBanner"
					style={{
						position: "absolute",
						width: "28%",
						height: "10%",
						left: "36%",
						top: "68%",
						display: "none",
					}}
				>
					<img id={"quizResultBannerImage"}
						className={"bannerImage"}
						draggable={false}
						src={passed ? "quiz_score/text_ribbon_green.png" : "quiz_score/text_ribbon_red.png"}
						width={"100%"}
						height={"auto"}
					/>
					<div id={"quizResultBannerText"}
						className={"fullScreen centerContent"}
						style={{
							fontSize: simulation ? 26 : (20 * props.scale) + "px",
							marginTop: "2%",
							zIndex: 1,
						}}
					>
						{
							passed ? "You passed!" : "You didn't pass"
						}
					</div>
				</div>
				{
					isGradedQuiz && anyGradedQuestions ? null :
						!showWarnings ? null :
							<Panel className={"uiPreviewWarning"}>
								{
									isGradedQuiz ? null :
										<div>
										Set the project to be a graded quiz in the Quiz Settings to enable quiz results
										</div>
								}
								<Spacer size={{ y: "12px" }} />
								{
									anyGradedQuestions ? null :
										<div>
										Add a graded quiz question to a step to enable quiz results
										</div>
								}
							</Panel>
				}
			</div>
		);
	}
}